import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import {
  createEmployeeApi,
  getEmployeeApi,
  updateEmployeeApi,
  UpsertEmployeePayload,
} from "../api/employeesApi";
import DependentRowsEditor, {
  DependentRow,
  emptyDependentRow,
  isBlankDependentRow,
  toDependentPayload,
  validateDependentRows,
} from "../components/DependentRowsEditor";
import {
  BANK_ACCOUNT_TYPE_LABELS,
  DpBankAccountType,
  DpGender,
  DpMaritalStatus,
  GENDER_LABELS,
  MARITAL_STATUS_LABELS,
  UF_OPTIONS,
} from "../api/dpTypes";
import { maskCpfInput, stripEmpty, toDateInputValue } from "../utils/format";
import { DP_EMPLOYEE_PAGE, DP_EMPLOYEES_PAGE, DP_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./DpEco.css";

/**
 * Estado do formulário: todo campo é string (é o que o `<input>` devolve). A
 * conversão para o payload acontece no submit.
 *
 * A lista espelha `CreateEmployeeDto` do backend, que roda sob `whitelist: true` —
 * chave que não existe lá é descartada em silêncio, sem erro. Campo novo entra
 * nos dois lugares na mesma entrega.
 */
type FormState = Record<string, string>;

const EMPTY: FormState = {
  fullName: "",
  socialName: "",
  cpf: "",
  birthDate: "",
  gender: "NAO_INFORMADO",
  maritalStatus: "",
  nationality: "",
  birthCity: "",
  birthState: "",
  motherName: "",
  fatherName: "",
  rg: "",
  rgIssuer: "",
  rgIssuedAt: "",
  ctpsNumber: "",
  ctpsSeries: "",
  ctpsState: "",
  pisNis: "",
  voterTitle: "",
  militaryCert: "",
  cnh: "",
  cnhCategory: "",
  cnhExpiresAt: "",
  email: "",
  phone: "",
  addressStreet: "",
  addressNumber: "",
  addressComplement: "",
  addressDistrict: "",
  addressCity: "",
  addressState: "",
  addressZip: "",
  bankCode: "",
  bankName: "",
  bankBranch: "",
  bankAccount: "",
  bankAccountType: "",
  pixKey: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  emergencyContactRelationship: "",
  notes: "",
};

export default function EmployeeFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  // Cancelar e salvar a edição voltam para onde o operador estava (ficha ou lista).
  const goBack = useGoBack(isEdit && id ? DP_EMPLOYEE_PAGE(id) : DP_EMPLOYEES_PAGE());

  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [form, setForm] = useState<FormState>(EMPTY);
  /** Filhos/dependentes — só no cadastro; na edição eles têm a aba própria da ficha. */
  const [dependentRows, setDependentRows] = useState<DependentRow[]>(() => [emptyDependentRow()]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  /** Seção destacada na navegação lateral (acompanha a rolagem). */
  const [activeSection, setActiveSection] = useState("identificacao");

  useEffect(() => {
    if (loading || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveSection(visible.target.id.replace("dpe-", ""));
      },
      { rootMargin: "-10% 0px -60% 0px" },
    );
    document.querySelectorAll(".dpeCardForm[id^='dpe-']").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading, isEdit]);

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const data = await getEmployeeApi(token, id);
      const next: FormState = { ...EMPTY };
      Object.keys(EMPTY).forEach((key) => {
        const value = (data as unknown as Record<string, unknown>)[key];
        if (value === null || value === undefined) return;
        next[key] = String(value);
      });
      // Campos de data vêm em ISO; o <input type="date"> quer YYYY-MM-DD.
      ["birthDate", "rgIssuedAt", "cnhExpiresAt"].forEach((key) => {
        next[key] = toDateInputValue(
          (data as unknown as Record<string, string | null>)[key]
        );
      });
      next.cpf = maskCpfInput(data.cpf ?? "");
      setForm(next);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o colaborador.");
      navigate(DP_EMPLOYEES_PAGE(), { replace: true });
    } finally {
      setLoading(false);
    }
  }, [token, id, navigate]);

  useEffect(() => {
    if (isEdit) load();
  }, [isEdit, load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (!form.fullName.trim()) {
      toast.error("Informe o nome completo.");
      return;
    }
    if (!form.cpf.trim()) {
      toast.error("Informe o CPF.");
      return;
    }
    if (!form.birthDate) {
      toast.error("Informe a data de nascimento.");
      return;
    }

    if (!isEdit) {
      const problem = validateDependentRows(dependentRows);
      if (problem) {
        toast.error(problem);
        return;
      }
    }

    // `stripEmpty` remove as chaves vazias: mandar "" em cada um dos ~40 campos
    // não preenchidos limparia dados que o operador não pediu para limpar.
    const payload = stripEmpty(form) as unknown as UpsertEmployeePayload;
    const dependents = dependentRows.filter((row) => !isBlankDependentRow(row)).map(toDependentPayload);

    setSaving(true);
    try {
      if (isEdit && id) {
        await updateEmployeeApi(token, id, payload);
        toast.success("Colaborador atualizado.");
        goBack();
      } else {
        // Colaborador e filhos no mesmo POST: o backend grava tudo ou nada.
        const created = await createEmployeeApi(token, {
          ...payload,
          ...(dependents.length ? { dependents } : {}),
        });
        toast.success("Colaborador cadastrado.");
        navigate(DP_EMPLOYEE_PAGE(created.id), { replace: true });
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };


  type Span = 1 | 2 | 3 | 4 | 6;
  const spanClass = (span: Span) => (span === 2 ? "ecoField" : `ecoField dpeSpan${span}`);

  const textField = (
    key: string,
    label: string,
    span: Span,
    extra?: { required?: boolean; placeholder?: string; type?: string; maxLength?: number }
  ) => (
    <div className={spanClass(span)} key={key}>
      <label htmlFor={key}>
        {label}
        {extra?.required ? <span className="dpeReq"> *</span> : null}
      </label>
      <input
        id={key}
        className="ecoInput ecoInput--sm"
        type={extra?.type ?? "text"}
        value={form[key]}
        placeholder={extra?.placeholder}
        maxLength={extra?.maxLength}
        onChange={(e) => set(key, e.target.value)}
        disabled={saving}
      />
    </div>
  );

  const selectField = (
    key: string,
    label: string,
    span: Span,
    options: { value: string; label: string }[],
    emptyLabel?: string
  ) => (
    <div className={spanClass(span)} key={key}>
      <label htmlFor={key}>{label}</label>
      <select
        id={key}
        className="ecoSelect ecoSelect--sm"
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        disabled={saving}
      >
        {emptyLabel !== undefined ? <option value="">{emptyLabel}</option> : null}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );

  const ufOptions = UF_OPTIONS.map((uf) => ({ value: uf, label: uf }));

  const sections = [
    { id: "identificacao", label: "Identificação" },
    { id: "documentos", label: "Documentos" },
    { id: "endereco", label: "Endereço" },
    { id: "bancarios", label: "Dados bancários" },
    { id: "emergencia", label: "Contato de emergência" },
    ...(!isEdit ? [{ id: "dependentes", label: "Filhos e dependentes" }] : []),
    { id: "observacoes", label: "Observações" },
  ];

  const scrollTo = (sectionId: string) => {
    setActiveSection(sectionId);
    document.getElementById(`dpe-${sectionId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!token) return null;

  const title = isEdit ? "Editar colaborador" : "Novo colaborador";

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
            { label: "Colaboradores", onClick: () => navigate(DP_EMPLOYEES_PAGE()) },
            { label: title },
          ]}
          title={title}
          subtitle={
            isEdit
              ? "Dados da pessoa. Contrato, dependentes e documentos ficam nas abas da ficha."
              : "Cadastro da pessoa. Só o nome, o CPF e a data de nascimento são obrigatórios; o resto pode ser completado depois. A admissão, com cargo e salário, é registrada na ficha."
          }
          onBack={goBack}
          backLabel="Cancelar"
        />

        {loading ? (
          <p className="dpePage__loading" role="status">Carregando...</p>
        ) : (
          <form className="dpeForm" onSubmit={handleSubmit} noValidate>
            <nav className="dpeForm__nav" aria-label="Seções do cadastro">
              {sections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`dpeForm__navLink${activeSection === s.id ? " is-active" : ""}`}
                  aria-current={activeSection === s.id ? "true" : undefined}
                  onClick={() => scrollTo(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </nav>

            <div className="dpeForm__sections">
              <section id="dpe-identificacao" className="ecoCard dpeCardForm" aria-labelledby="dpe-h-identificacao">
                <h2 id="dpe-h-identificacao" className="dpeCardForm__title">Identificação</h2>
                <div className="dpeFields">
                  {textField("fullName", "Nome completo", 4, { required: true, maxLength: 200, placeholder: "Como está no documento" })}
                  {textField("socialName", "Nome social", 2, { maxLength: 200 })}

                  <div className="ecoField">
                    <label htmlFor="cpf">
                      CPF<span className="dpeReq"> *</span>
                    </label>
                    <input
                      id="cpf"
                      className="ecoInput ecoInput--sm"
                      value={form.cpf}
                      onChange={(e) => set("cpf", maskCpfInput(e.target.value))}
                      placeholder="000.000.000-00"
                      inputMode="numeric"
                      disabled={saving}
                    />
                    {isEdit ? <span className="dpeHint">Só pode ser alterado enquanto não houver contrato.</span> : null}
                  </div>

                  {textField("birthDate", "Data de nascimento", 2, { required: true, type: "date" })}
                  {selectField(
                    "gender",
                    "Gênero",
                    2,
                    (Object.keys(GENDER_LABELS) as DpGender[]).map((g) => ({ value: g, label: GENDER_LABELS[g] }))
                  )}
                  {selectField(
                    "maritalStatus",
                    "Estado civil",
                    2,
                    (Object.keys(MARITAL_STATUS_LABELS) as DpMaritalStatus[]).map((m) => ({ value: m, label: MARITAL_STATUS_LABELS[m] })),
                    "—"
                  )}
                  {textField("nationality", "Nacionalidade", 2, { maxLength: 60 })}
                  {textField("birthCity", "Cidade de nascimento", 2, { maxLength: 80 })}
                  {selectField("birthState", "UF de nascimento", 2, ufOptions, "—")}
                  {textField("motherName", "Nome da mãe", 3, { maxLength: 200 })}
                  {textField("fatherName", "Nome do pai", 3, { maxLength: 200 })}
                  {textField("email", "E-mail", 3, { type: "email", maxLength: 255 })}
                  {textField("phone", "Telefone", 3, { maxLength: 20 })}
                </div>
                <p className="dpeCardForm__note">
                  CPF e nascimento são obrigatórios no cadastro. Sem CPF o colaborador não aceita admissão nem acesso ao portal.
                </p>
              </section>

              <section id="dpe-documentos" className="ecoCard dpeCardForm" aria-labelledby="dpe-h-documentos">
                <h2 id="dpe-h-documentos" className="dpeCardForm__title">Documentos</h2>
                <div className="dpeFields">
                  {textField("rg", "RG", 2, { maxLength: 20 })}
                  {textField("rgIssuer", "Órgão emissor", 2, { maxLength: 20 })}
                  {textField("rgIssuedAt", "Emissão do RG", 2, { type: "date" })}
                  {textField("ctpsNumber", "CTPS, número", 2, { maxLength: 20 })}
                  {textField("ctpsSeries", "CTPS, série", 2, { maxLength: 10 })}
                  {selectField("ctpsState", "CTPS, UF", 2, ufOptions, "—")}
                  {textField("pisNis", "PIS/NIS/PASEP", 2, { placeholder: "Pode digitar com pontuação" })}
                  {textField("voterTitle", "Título de eleitor", 2)}
                  {textField("militaryCert", "Certificado de reservista", 2, { maxLength: 20 })}
                  {textField("cnh", "CNH", 2, { maxLength: 11 })}
                  {textField("cnhCategory", "CNH, categoria", 2, { maxLength: 5 })}
                  {textField("cnhExpiresAt", "CNH, validade", 2, { type: "date" })}
                </div>
              </section>

              <section id="dpe-endereco" className="ecoCard dpeCardForm" aria-labelledby="dpe-h-endereco">
                <h2 id="dpe-h-endereco" className="dpeCardForm__title">Endereço</h2>
                <div className="dpeFields">
                  {textField("addressZip", "CEP", 2, { placeholder: "00000-000" })}
                  {textField("addressStreet", "Logradouro", 4, { maxLength: 120 })}
                  {textField("addressNumber", "Número", 1, { maxLength: 20 })}
                  {textField("addressComplement", "Complemento", 2, { maxLength: 60 })}
                  {textField("addressDistrict", "Bairro", 3, { maxLength: 80 })}
                  {textField("addressCity", "Cidade", 4, { maxLength: 80 })}
                  {selectField("addressState", "UF", 2, ufOptions, "—")}
                </div>
              </section>

              <section id="dpe-bancarios" className="ecoCard dpeCardForm" aria-labelledby="dpe-h-bancarios">
                <h2 id="dpe-h-bancarios" className="dpeCardForm__title">Dados bancários</h2>
                <div className="dpeFields">
                  {textField("bankCode", "Código do banco", 1, { maxLength: 5 })}
                  {textField("bankName", "Banco", 3, { maxLength: 80 })}
                  {textField("bankBranch", "Agência", 2, { maxLength: 10 })}
                  {textField("bankAccount", "Conta", 2, { maxLength: 20 })}
                  {selectField(
                    "bankAccountType",
                    "Tipo de conta",
                    2,
                    (Object.keys(BANK_ACCOUNT_TYPE_LABELS) as DpBankAccountType[]).map((t) => ({ value: t, label: BANK_ACCOUNT_TYPE_LABELS[t] })),
                    "—"
                  )}
                  {textField("pixKey", "Chave PIX", 2, { maxLength: 120 })}
                </div>
              </section>

              <section id="dpe-emergencia" className="ecoCard dpeCardForm" aria-labelledby="dpe-h-emergencia">
                <h2 id="dpe-h-emergencia" className="dpeCardForm__title">Contato de emergência</h2>
                <div className="dpeFields">
                  {textField("emergencyContactName", "Nome", 2, { maxLength: 200 })}
                  {textField("emergencyContactPhone", "Telefone", 2, { maxLength: 20 })}
                  {textField("emergencyContactRelationship", "Parentesco", 2, { maxLength: 40 })}
                </div>
              </section>

              {!isEdit ? (
                <section id="dpe-dependentes" className="ecoCard dpeCardForm" aria-labelledby="dpe-h-dependentes">
                  <h2 id="dpe-h-dependentes" className="dpeCardForm__title">Filhos e dependentes</h2>
                  <p className="dpeCardForm__note">
                    Opcional. A data de nascimento é o que permite ao sistema conferir o salário-família (filhos com
                    menos de 14 anos). Linhas em branco são ignoradas; o colaborador e os filhos são gravados juntos,
                    ou nada é gravado. Depois do cadastro, os dependentes ficam na aba "Dependentes" da ficha.
                  </p>
                  <DependentRowsEditor rows={dependentRows} onChange={setDependentRows} disabled={saving} variant="eco" />
                </section>
              ) : null}

              <section id="dpe-observacoes" className="ecoCard dpeCardForm" aria-labelledby="dpe-h-observacoes">
                <h2 id="dpe-h-observacoes" className="dpeCardForm__title">Observações</h2>
                <div className="ecoField">
                  <label htmlFor="notes" className="ecoVisuallyHidden">Observações internas</label>
                  <textarea
                    id="notes"
                    className="ecoInput dpeTextarea"
                    value={form.notes}
                    maxLength={1000}
                    placeholder="Observações internas do DP"
                    onChange={(e) => set("notes", e.target.value)}
                    disabled={saving}
                  />
                </div>
              </section>

              <div className="dpeForm__actions">
                <button type="button" className="ecoBtn" onClick={goBack} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="ecoBtn ecoBtn--primary" disabled={saving}>
                  {saving ? "Salvando..." : isEdit ? "Salvar alterações" : "Salvar e abrir a ficha"}
                </button>
              </div>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
